import React, { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { matchPath } from 'react-router'
import { Sidebar } from 'rsuite'
import Sidenav from 'rsuite/Sidenav'
import Nav from 'rsuite/Nav'
import AngleDownIcon from '@rsuite/icons/legacy/AngleDown'
import SignOutIcon from '@rsuite/icons/legacy/SignOut'
import MenuItems, { MENU_SECTIONS } from 'config/MenuItems'
import tenantConfig from 'config/tenant'
import { Divider } from 'shared/atoms'
import ConfirmDialog from '../confirmDialog/ConfirmDialog'
import { useAuth } from 'contexts/AuthContext'
import { notifySuccess } from 'utils'
import NavItem from './NavItem'
import './sideNav.scss'

interface SideNavProps {
  /** Phone-only: whether the off-canvas nav is slid in. Ignored on desktop. */
  mobileOpen?: boolean;
  /** Phone-only: called when a nav item is tapped so the parent can close. */
  onMobileClose?: () => void;
}

const SideNav: React.FC<SideNavProps> = ({ mobileOpen = false, onMobileClose }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { roles, hasAnyPermission, isModuleEnabled, logout } = useAuth()
  const [activeKey, setActiveKey] = React.useState('')
  // Confirm sign-out before clearing the session, so an accidental click
  // doesn't log the user out. Cancel just closes the dialog.
  const [signOutOpen, setSignOutOpen] = React.useState(false)

  const handleSignOut = () => {
    onMobileClose?.()
    logout()
    navigate('/login', { replace: true })
    notifySuccess('Signed out successfully')
  }

  // SUPER_ADMIN owns module configuration, so they need to see every item even
  // when the module is currently disabled — otherwise they couldn't navigate
  // to the screens that turn things on. ADMIN and below stay gated.
  const isSuperAdmin = roles.includes('ROLE_SUPER_ADMIN')

  // Item shows iff role + permission + module gates all pass. A gate is skipped
  // when the corresponding field is omitted on the menu entry, and the module
  // gate is skipped entirely for SUPER_ADMIN.
  const visibleItems = React.useMemo(() => {
    const passesRole = (item: (typeof MenuItems)[number]) =>
      !item.allowedRoles ||
      item.allowedRoles.length === 0 ||
      item.allowedRoles.some((r) => roles.includes(r))
    const passesPermission = (item: (typeof MenuItems)[number]) =>
      !item.requiredPermissions ||
      item.requiredPermissions.length === 0 ||
      hasAnyPermission(item.requiredPermissions)
    const passesModule = (item: (typeof MenuItems)[number]) =>
      isSuperAdmin ||
      !item.requiredModule ||
      isModuleEnabled(item.requiredModule)
    return MenuItems.filter(
      (i) => passesRole(i) && passesPermission(i) && passesModule(i)
    )
  }, [roles, hasAnyPermission, isModuleEnabled, isSuperAdmin])

  // Group the (already gated) items under their section headers, in the order
  // declared by MENU_SECTIONS. Empty sections are dropped so a header never
  // shows without items. Any item lacking a known section falls into a trailing
  // header-less bucket so it can never be silently hidden.
  const groupedSections = React.useMemo(() => {
    const known = new Set(MENU_SECTIONS.map((s) => s.id))
    const sections = MENU_SECTIONS.map((section) => ({
      id: section.id,
      title: section.title,
      icon: section.icon,
      items: visibleItems.filter((i) => i.section === section.id)
    })).filter((s) => s.items.length > 0)
    const orphans = visibleItems.filter(
      (i) => !i.section || !known.has(i.section)
    )
    return { sections, orphans }
  }, [visibleItems])

  useEffect(() => {
    const currentPath = location.pathname

    const findActiveMenuItem = (
      items: typeof MenuItems,
      path: string
    ): string | undefined => {
      // Sort by link length (longest first) so a more specific entry like
      // `/admin/modules` wins over a shorter prefix like `/admin` when both
      // exist. Without this, prefix matching could highlight the wrong tab.
      const sorted = [...items].sort(
        (a, b) => (b.link?.length ?? 0) - (a.link?.length ?? 0)
      )
      for (const item of sorted) {
        // `end: false` lets `/plots` match `/plots/123`, so detail pages keep
        // the right sidebar tab highlighted.
        if (item.link && matchPath({ path: item.link, end: false }, path)) {
          return item.id
        }
        if (item.matchPaths?.some((p) => matchPath({ path: p, end: false }, path))) {
          return item.id
        }
        if (item.children) {
          const childActiveKey = findActiveMenuItem(item.children, path)
          if (childActiveKey) {
            return item.id
          }
        }
      }
      return undefined
    }

    const activeMenuItemId = findActiveMenuItem(MenuItems, currentPath)
    if (activeMenuItemId) {
      setActiveKey(activeMenuItemId)
    }
  }, [location.pathname])

  // Which titled sections are expanded. Collapsed by default so the nav stays
  // short; a section auto-opens when the current route is one of its items, so
  // landing on /branding reveals (and highlights) it inside Settings.
  const [openSections, setOpenSections] = React.useState<
    Record<string, boolean>
  >({})
  const toggleSection = (id: string) =>
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }))

  const activeSectionId = React.useMemo(
    () =>
      groupedSections.sections.find((s) =>
        s.items.some((i) => i.id === activeKey)
      )?.id,
    [groupedSections.sections, activeKey]
  )
  React.useEffect(() => {
    if (activeSectionId) {
      setOpenSections((prev) =>
        prev[activeSectionId] ? prev : { ...prev, [activeSectionId]: true }
      )
    }
  }, [activeSectionId])

  // Sidebar starts open. It collapses/expands only via the toggle button —
  // clicking elsewhere on the page leaves it as-is.
  const [expanded, setExpanded] = React.useState(true)

  // On phones the off-canvas nav must always show labels when slid in, so
  // suppress the `collapsing` (icon-only) class while `mobile-open`. On desktop
  // `mobileOpen` is always false, so the collapse logic is unchanged.
  const collapsed = !expanded && !mobileOpen
  let sidebarClass = expanded ? '' : 'collapsing'
  if (mobileOpen) sidebarClass = 'mobile-open'

  // ── Collapsed-rail flyout ────────────────────────────────────────────────
  // A 56px rail has no room for labels, so a section can't expand in place —
  // its items would become a run of anonymous icons indistinguishable from the
  // top-level ones. Instead the section icon opens a labelled flyout pinned
  // beside it (the standard mini-rail pattern). `top` is captured from the
  // trigger so the panel lines up with the icon that opened it; `fixed`
  // positioning keeps it clear of the rail's `overflow: hidden`.
  const [flyout, setFlyout] = React.useState<{ id: string, top: number } | null>(
    null
  )
  const flyoutRef = React.useRef<HTMLDivElement>(null)
  const closeFlyout = React.useCallback(() => setFlyout(null), [])

  // "Settings" sits at the bottom of the rail, so a panel opened flush with its
  // icon would hang off the viewport. Nudge it up once its real height is known.
  React.useLayoutEffect(() => {
    const el = flyoutRef.current
    if (!flyout || !el) return
    const gutter = 12
    const maxTop = window.innerHeight - el.offsetHeight - gutter
    el.style.top = `${Math.max(gutter, Math.min(flyout.top, maxTop))}px`
  }, [flyout])

  const clickToggler = () => {
    closeFlyout()
    setExpanded(!expanded)
  }

  const handleSectionClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    sectionId: string
  ) => {
    if (!collapsed) {
      toggleSection(sectionId)
      return
    }
    const { top } = e.currentTarget.getBoundingClientRect()
    setFlyout((prev) => (prev?.id === sectionId ? null : { id: sectionId, top }))
  }

  // Dismiss on outside click, Escape, scroll or resize — anything that would
  // leave the panel stranded away from its trigger. Clicks on the trigger fall
  // through to its own handler so it keeps toggling.
  React.useEffect(() => {
    if (!flyout) return undefined
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Element | null
      if (target?.closest('.sidenav-flyout, .sidenav-section-toggle')) return
      closeFlyout()
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeFlyout()
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    window.addEventListener('resize', closeFlyout)
    // Capture phase so scrolling the rail itself counts, not just the window.
    document.addEventListener('scroll', closeFlyout, true)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('resize', closeFlyout)
      document.removeEventListener('scroll', closeFlyout, true)
    }
  }, [flyout, closeFlyout])

  const flyoutSection = React.useMemo(
    () => groupedSections.sections.find((s) => s.id === flyout?.id),
    [groupedSections.sections, flyout]
  )

  return (
    <Sidebar
      className={sidebarClass}
      data-testid="sidebar"
    >
      <div className="sideNav">
        <Sidenav appearance="subtle" expanded={expanded || mobileOpen}>
          <div content="Global controls 1.0" className="rs-sidenav-toggle">
            <h3 className="sidenav-header">{tenantConfig.name}</h3>
            <button
              aria-label="Collapse"
              type="button"
              className="rs-sidenav-toggle-button rs-btn-icon rs-btn-icon-placement-left rs-btn rs-btn-default"
              onClick={clickToggler}
            >
              {expanded ? (
                <svg
                  width="1em"
                  height="1em"
                  viewBox="0 0 12 32"
                  fill="currentColor"
                  aria-hidden="true"
                  focusable="false"
                  className="rs-icon"
                  aria-label="angle left"
                  data-category="legacy"
                >
                  <path d="M11.196 9.714a.612.612 0 01-.179.411l-7.018 7.018 7.018 7.018c.107.107.179.268.179.411s-.071.304-.179.411l-.893.893c-.107.107-.268.179-.411.179s-.304-.071-.411-.179L.981 17.555c-.107-.107-.179-.268-.179-.411s.071-.304.179-.411l8.321-8.321c.107-.107.268-.179.411-.179s.304.071.411.179l.893.893c.107.107.179.25.179.411z" />
                </svg>
              ) : (
                <svg
                  width="1em"
                  height="1em"
                  viewBox="0 0 12 32"
                  fill="currentColor"
                  aria-hidden="true"
                  focusable="false"
                  className="rs-icon"
                  aria-label="angle right"
                  data-category="legacy"
                >
                  <path d="M.804 22.286a.612.612 0 01.179-.411l7.018-7.018-7.018-7.018c-.107-.107-.179-.268-.179-.411s.071-.304.179-.411l.893-.893c.107-.107.268-.179.411-.179s.304.071.411.179l8.321 8.321c.107.107.179.268.179.411s-.071.304-.179.411l-8.321 8.321c-.107.107-.268.179-.411.179s-.304-.071-.411-.179l-.893-.893c-.107-.107-.179-.25-.179-.411z" />
                </svg>
              )}
            </button>
          </div>
          <Sidenav.Body>
            <Nav
              activeKey={activeKey}
              onSelect={(key) => {
                setActiveKey(key)
                onMobileClose?.()
              }}
            >
              {/* Ungrouped items render flat, in order, at the top… */}
              {groupedSections.orphans.map(({ id, name, link, icon }) => (
                <NavItem
                  key={id}
                  id={id}
                  name={name}
                  link={link}
                  icon={icon}
                  active={activeKey === id}
                />
              ))}
              {/* …then each titled section (only "Settings" today) at the
                  bottom, rendered as a collapsible entry that looks exactly
                  like a menu item. Its children mount only while open. */}
              {groupedSections.sections.map((section) => {
                // Collapsed, the group never expands in place — the flyout owns
                // that job, so `open` only drives the inline list on the wide rail.
                const open = !collapsed && !!openSections[section.id]
                const isActiveSection = section.id === activeSectionId
                const SectionIcon = section.icon
                return (
                  <React.Fragment key={section.id}>
                    <button
                      type="button"
                      className={`sidenav-section-toggle${
                        open ? ' sidenav-section-toggle--open' : ''
                      }${
                        !open && isActiveSection
                          ? ' sidenav-section-toggle--active'
                          : ''
                      }${
                        flyout?.id === section.id
                          ? ' sidenav-section-toggle--flyout'
                          : ''
                      }`}
                      aria-expanded={collapsed ? flyout?.id === section.id : open}
                      aria-haspopup={collapsed ? 'menu' : undefined}
                      onClick={(e) => handleSectionClick(e, section.id)}
                      title={section.title}
                    >
                      <SectionIcon className="rs-sidenav-item-icon rs-icon" />
                      <span className="sidenav-item__text">{section.title}</span>
                      <AngleDownIcon className="sidenav-section-toggle__caret" />
                    </button>
                    {open &&
                      section.items.map(({ id, name, link, icon }) => (
                        <NavItem
                          key={id}
                          id={id}
                          name={name}
                          link={link}
                          icon={icon}
                          active={activeKey === id}
                        />
                      ))}
                  </React.Fragment>
                )
              })}
              <Divider />
            </Nav>
          </Sidenav.Body>
          {/* Sign-out anchored at the very bottom of the sidebar — sits
              outside Sidenav.Body so it stays pinned even when the menu
              list scrolls, and never inherits the "active" highlight. Also
              offered from the header's avatar menu. */}
          <div className="sidenav-footer">
            <button
              type="button"
              className="sidenav-signout"
              onClick={() => setSignOutOpen(true)}
              title="Sign out"
            >
              <SignOutIcon className="rs-sidenav-item-icon rs-icon" />
              <span className="sidenav-item__text">Sign out</span>
            </button>
          </div>
        </Sidenav>
      </div>

      {/* Collapsed-rail submenu — labelled list for the section whose icon was
          clicked. Rendered outside the scrolling body so it can overhang the rail. */}
      {collapsed && flyout && flyoutSection && (
        <div
          ref={flyoutRef}
          className="sidenav-flyout"
          style={{ top: flyout.top }}
          role="menu"
          aria-label={flyoutSection.title}
        >
          <div className="sidenav-flyout__title">{flyoutSection.title}</div>
          {flyoutSection.items.map(({ id, name, link, icon: Icon }) => (
            <button
              key={id}
              type="button"
              role="menuitem"
              className={`sidenav-flyout__item${
                activeKey === id ? ' sidenav-flyout__item--active' : ''
              }`}
              onClick={() => {
                setActiveKey(id)
                closeFlyout()
                if (link) navigate(link)
              }}
            >
              <Icon className="rs-sidenav-item-icon rs-icon" />
              <span>{name}</span>
            </button>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={signOutOpen}
        type="warning"
        icon={SignOutIcon}
        title="Sign out"
        message="Are you sure you want to sign out?"
        confirmText="Sign out"
        cancelText="Cancel"
        onConfirm={handleSignOut}
        onCancel={() => setSignOutOpen(false)}
      />
    </Sidebar>
  )
}

export default SideNav
